/**
 * Helpers for the NativeHandDetails view.
 */

const SUIT_SYMBOL_TO_NAME = {
  "\u2660": "spades",
  "\u2665": "hearts",
  "\u2663": "clubs",
  "\u2666": "diamonds",
};

export function rawCardToSprite(rawId) {
  if (typeof rawId !== "number" || rawId <= 0) return null;
  const suitIdx = Math.floor(rawId / 256);
  const rank = rawId % 256;
  const SUITS = ["spades", "hearts", "clubs", "diamonds"];
  const suit = SUITS[suitIdx] || null;
  if (!suit) return null;
  if (rank < 2 || rank > 14) return null;
  const rankStr =
    rank === 14 ? "A" : rank === 13 ? "K" : rank === 12 ? "Q" : rank === 11 ? "J" : String(rank);
  return { rank: rankStr, suit };
}

export function labelToSprite(label) {
  if (typeof label !== "string" || label.length < 2) return null;
  const lastChar = label.slice(-1);
  const suit = SUIT_SYMBOL_TO_NAME[lastChar];
  if (!suit) return null;
  const rank = label.slice(0, -1);
  if (!rank) return null;
  return { rank, suit };
}

export function cardToSpriteUrl(backendUrl, slot) {
  if (slot == null) return null;
  let r;
  if (typeof slot === "object") {
    if (slot.label) r = labelToSprite(slot.label);
    if (!r && typeof slot.raw === "number") r = rawCardToSprite(slot.raw);
  } else if (typeof slot === "string") {
    r = labelToSprite(slot);
  } else if (typeof slot === "number") {
    r = rawCardToSprite(slot);
  }
  if (!r) return null;
  return `${backendUrl}/api/v2/native-ui/cards/${r.rank}_${r.suit}.png`;
}

export function decodeBoard(backendUrl, board) {
  if (!Array.isArray(board)) return [];
  return board
    .map((item) => {
      const url = cardToSpriteUrl(backendUrl, item);
      if (!url) return null;
      const sprite =
        typeof item === "string"
          ? labelToSprite(item)
          : typeof item === "number"
          ? rawCardToSprite(item)
          : item.label
          ? labelToSprite(item.label)
          : rawCardToSprite(item.raw);
      return { ...sprite, url };
    })
    .filter(Boolean);
}

export const POSITION_LABELS = ["SB", "BB", "UTG", "UTG+1", "UTG+2", "MP", "LJ", "HJ", "CO", "BTN", "D"];
export function normalizePosition(p) {
  if (typeof p === "string") {
    const up = p.toUpperCase().trim();
    if (up === "D" || up === "BUTTON" || up === "BTN") return "BTN";
    if (up.startsWith("UTG")) return up.replace(/\s+/g, "");
    if (POSITION_LABELS.includes(up)) return up;
  }
  if (typeof p === "number") {
    const map = ["SB", "BB", "UTG", "UTG+1", "UTG+2", "MP", "HJ", "CO", "BTN"];
    return map[p] || `P${p}`;
  }
  return p ?? "—";
}

/**
 * Canonical action key normalization.
 * Maps any input form (UPPER/lower/snake_case/Mixed) to a stable lowercase key
 * matching ActionChip.ACTION_STYLE keys.
 */
export function canonicalAction(action) {
  if (!action) return "";
  // Allin override (visual priority over base action)
  if (action.is_allin === true) return "allin";
  const ca = action.canonical_action || action.action_name || action.action_type;
  if (typeof ca !== "string") {
    const actionType = Number(action.action_type ?? action.actionType);
    const NUMERIC_ACTIONS = {
      0: "fold",
      1: "call",
      2: "check",
      3: "bet",
      4: "raise",
      5: "allin",
      17: "bb",
    };
    return NUMERIC_ACTIONS[actionType] || "";
  }
  const key = ca.toLowerCase().replace(/[\s-]/g, "_");
  // Aliases
  const ALIASES = {
    small_blind: "sb",
    smallblind: "sb",
    sb_blind: "sb",
    big_blind: "bb",
    bigblind: "bb",
    bomb_pot_bb: "bb",
    bombpot_bb: "bb",
    bomb_pot_blind: "bb",
    pot: "raise",
    pot_bet: "raise",
    pre_raise: "raise",
    re_raise: "raise",
    reraise: "raise",
    pre_flop_raise: "raise",
    "all_in": "allin",
    "all-in": "allin",
    showdown: "show",
  };
  return ALIASES[key] || key;
}

export function stageLabel(stage, street) {
  if (street) return street.charAt(0).toUpperCase() + street.slice(1);
  switch (Number(stage)) {
    case 1:
      return "Preflop";
    case 2:
      return "Flop";
    case 3:
      return "Turn";
    case 4:
      return "River";
    case 5:
      return "Showdown";
    default:
      return `Stage ${stage}`;
  }
}

export function fmtChips(chips, opts = {}) {
  const { suffix = "", placeholder = "—" } = opts;
  if (chips == null || Number.isNaN(Number(chips))) return placeholder;
  const n = Number(chips);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n) + suffix;
}

/**
 * Infer chip_factor (raw or chips → visual) from a hand's BIG_BLIND action.
 *
 * IMPORTANT: el persister calcula `amount_chips = amount_raw * 120` (escala interna).
 * Pero el cliente nativo muestra `chips / 10^k` con BB en rango [10, 100).
 * Inferimos la potencia de 10 que lleva el BB a ese rango.
 *
 * Examples:
 *   BB amount_chips = 120,000,000 → factor = 10,000,000 → BB shown as 12 ✓
 *   BB amount_chips = 12,000      → factor = 1,000      → BB shown as 12
 *   BB amount_raw = 6 (already visual) → factor = 1     → BB shown as 6
 */
export function inferChipFactor(handData) {
  if (!handData) return 1;
  const actions = handData.actions || [];
  let bbValue = 0;
  for (const a of actions) {
    const ca = (a.canonical_action || a.action_name || "").toUpperCase();
    if (ca === "BIG_BLIND" || ca === "BB") {
      // Prefer amount_chips (post-persister) over amount_raw
      bbValue = Number(a.amount_chips) || Number(a.amount_raw) || 0;
      if (bbValue > 0) break;
    }
  }
  if (!bbValue) {
    bbValue =
      Number(handData.summary?.big_blind_chips) ||
      Number(handData.hand?.big_blind_chips) ||
      Number(handData.summary?.big_blind_raw) ||
      Number(handData.hand?.big_blind_raw) ||
      0;
  }
  if (!bbValue || bbValue <= 0) return 1;
  // Find power of 10 that lands BB into [5, 50). Typical PPPoker BBs: 6, 12, 25, 50.
  let factor = 1;
  let bb = bbValue;
  while (bb >= 50) {
    bb /= 10;
    factor *= 10;
  }
  return factor;
}

/**
 * Format chips applying inferred chip_factor.
 * If chips_chips field exists (already visual), use it directly. Otherwise convert raw.
 */
export function fmtChipsVisual(action, factor = 1, opts = {}) {
  const { placeholder = "" } = opts;
  if (!action) return placeholder;
  // Prefer pre-computed visual chips if present and non-zero
  const chipsField = action.amount_chips ?? action.chips_chips ?? action.stack_after_chips;
  if (chipsField != null && Number(chipsField) > 0) {
    return fmtChips(chipsField, { placeholder });
  }
  // Otherwise apply factor to raw
  const raw = action.amount_raw ?? action.chips_raw ?? action.stack_after_raw;
  if (raw == null || raw === 0) return placeholder;
  const visual = Number(raw) / factor;
  return fmtChips(visual, { placeholder });
}

/**
 * Apply factor to a single number (raw → visual).
 */
export function applyFactor(value, factor = 1) {
  if (value == null) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n / factor;
}

/**
 * displayChips — convierte un valor (chips o raw) a visual usando el factor inferido.
 *
 * Lógica: si el valor es "grande" (>= 1000) y el factor > 1, lo divide.
 * Si el valor ya es chico (≤999) lo asume ya en escala visual y lo retorna tal cual.
 * Si el factor es 1 (no hay reescalado), retorna el valor directo.
 */
export function displayChips(value, factor = 1) {
  if (value == null) return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  if (factor > 1 && Math.abs(n) >= 1000) return n / factor;
  return n;
}

/**
/**
 * Resolve the chips amount for an action row (any unit).
 * Caller is responsible for applying chipFactor when needed.
 *
 * ⭐ Prioriza `amount_in_street_chips` (preprocessed por NativeHandDetails)
 * que representa el TOTAL acumulado del jugador en la calle. PPPoker nativo
 * muestra ese "to amount" en vez del delta individual.
 */
export function actionAmount(action) {
  if (!action) return null;
  return (
    action.amount_in_street_chips ??
    action.amount_chips ??
    action.chips_chips ??
    action.amount_bb ??
    action.amount ??
    action.amount_raw ??
    action.chips_raw ??
    null
  );
}

export function actionStackAfter(action) {
  if (!action) return null;
  return (
    action.stack_after_chips ??
    action.left_chips_chips ??
    action.stack_after_bb ??
    action.stack_after ??
    action.stack_after_raw ??
    action.left_chips_raw ??
    null
  );
}
